import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  GpsFixed as TargetIcon,
  Language as GlobeIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { INDIGO, SLATE, EMERALD } from "../../constants/Colors";

const STEPS = [
  {
    id: "agent-purpose",
    label: "Agent Purpose",
    description: "Define what your agent does",
    icon: TargetIcon,
  },
  {
    id: "knowledge-source",
    label: "Knowledge Source",
    description: "Share website URLs for training",
    icon: GlobeIcon,
  },
  {
    id: "setup-channels",
    label: "Setup & Channels",
    description: "Select capabilities and channels",
    icon: SettingsIcon,
  },
  {
    id: "complete",
    label: "Complete",
    description: "Agent ready for testing",
    icon: CheckCircleIcon,
  },
];

// Determine step status relative to the active step index
function getStepStatus(stepIndex, activeIndex) {
  if (stepIndex < activeIndex) return "done";
  if (stepIndex === activeIndex) return "active";
  return "pending";
}

function StepIcon({ StepIconComponent, status }) {
  const bgColor =
    status === "active"
      ? INDIGO[600]
      : status === "done"
      ? EMERALD[500]
      : SLATE[300];

  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        bgcolor: bgColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "background-color 0.2s",
      }}
    >
      <StepIconComponent sx={{ fontSize: "1rem", color: "#fff" }} />
    </Box>
  );
}

function StepItem({ step, status, isLast, expanded }) {
  const isActive = status === "active";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          width: "100%",
          p: "10px 12px",
          borderRadius: "10px",
          bgcolor: isActive ? INDIGO[50] : "transparent",
          border: isActive ? `1.5px solid ${INDIGO[100]}` : "1.5px solid transparent",
          cursor: "default",
          transition: "all 0.2s",
        }}
      >
        <StepIcon StepIconComponent={step.icon} status={status} />
        {expanded && (
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? INDIGO[600] : SLATE[700],
                  lineHeight: 1.3,
                }}
              >
                {step.label}
              </Typography>
              {isActive && (
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: INDIGO[500],
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
            {isActive && (
              <Typography
                sx={{ fontSize: "0.75rem", color: INDIGO[500], mt: "1px" }}
              >
                In Progress
              </Typography>
            )}
            {!isActive && (
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: SLATE[400],
                  mt: "1px",
                  whiteSpace: "normal",
                  lineHeight: 1.3,
                }}
              >
                {step.description}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Connector line between steps */}
      {!isLast && (
        <Box
          sx={{
            width: 2,
            height: 20,
            bgcolor: SLATE[200],
            my: "2px",
            ml: expanded ? "29px" : 0,
            alignSelf: expanded ? "flex-start" : "center",
          }}
        />
      )}
    </Box>
  );
}

export default function ChatSidebar({ expanded, onToggle, activeStepIndex = 0 }) {
  return (
    <Box
      sx={{
        width: expanded ? 248 : 72,
        flexShrink: 0,
        bgcolor: "#fff",
        borderRight: `1px solid ${SLATE[200]}`,
        display: "flex",
        flexDirection: "column",
        transition: "width 0.25s ease",
        overflow: "hidden",
      }}
    >
      {/* Collapse / expand toggle */}
      <Box
        sx={{
          display: "flex",
          justifyContent: expanded ? "flex-end" : "center",
          px: expanded ? 1 : 0,
          pt: 1.5,
          pb: 1,
        }}
      >
        <IconButton
          onClick={onToggle}
          size="small"
          sx={{
            color: SLATE[500],
            "&:hover": { bgcolor: SLATE[100] },
            borderRadius: "8px",
          }}
        >
          <ChevronLeftIcon
            sx={{
              fontSize: "1.25rem",
              transform: expanded ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.25s ease",
            }}
          />
        </IconButton>
      </Box>

      {/* Header label — only when expanded */}
      {expanded && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography
            sx={{ fontSize: "0.875rem", fontWeight: 700, color: SLATE[900] }}
          >
            Build Progress
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: SLATE[500], mt: "2px" }}>
            Configure your AI agent
          </Typography>
        </Box>
      )}

      {/* Steps list */}
      <Box sx={{ px: expanded ? 1.5 : 1, display: "flex", flexDirection: "column" }}>
        {STEPS.map((step, index) => (
          <StepItem
            key={step.id}
            step={step}
            status={getStepStatus(index, activeStepIndex)}
            isLast={index === STEPS.length - 1}
            expanded={expanded}
          />
        ))}
      </Box>
    </Box>
  );
}
